import DOMPurify from 'dompurify';

/**
 * Теги, разрешённые в содержимом редактора.
 *
 * Список намеренно консервативный: форматирование, которое не переживёт санитизацию
 * на стороне просмотра, не должно попадать в данные — иначе пользователь оформит текст,
 * а при чтении оформление молча пропадёт.
 */
export const RICH_TEXT_ALLOWED_TAGS = ['p', 'strong', 'em', 'b', 'i', 'br', 'ul', 'ol', 'li', 'span', 'div'];

/** Форматы Quill, соответствующие {@link RICH_TEXT_ALLOWED_TAGS} */
export const RICH_TEXT_FORMATS = ['bold', 'italic', 'list'];

/** Служебные атрибуты Quill, которые не должны попадать в сохраняемый HTML */
const SERVICE_ATTRIBUTES = ['data-list', 'class', 'contenteditable', 'spellcheck'];

/** Очищает HTML от всего, что не входит в {@link RICH_TEXT_ALLOWED_TAGS} */
export const sanitizeRichTextHtml = (html: string): string =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS });

/**
 * Разбивает `<ol>` на последовательности `<ul>`/`<ol>` по атрибуту `data-list` его пунктов.
 *
 * Quill 2 хранит списки любого типа как `<ol>`, а вид маркера держит в `data-list` на `<li>`.
 * Без этого преобразования маркированный список сохранился бы как нумерованный.
 */
const normalizeLists = (root: HTMLElement): void => {
  const doc = root.ownerDocument;

  for (const list of Array.from(root.querySelectorAll('ol'))) {
    const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
    if (items.length === 0) continue;

    // Группируем подряд идущие пункты одного вида — один список может смешивать маркеры
    const groups: { tag: 'ul' | 'ol'; items: Element[] }[] = [];
    for (const item of items) {
      const tag = item.getAttribute('data-list') === 'bullet' ? 'ul' : 'ol';
      const lastGroup = groups.at(-1);
      if (lastGroup?.tag === tag) {
        lastGroup.items.push(item);
      } else {
        groups.push({ tag, items: [item] });
      }
    }

    const fragment = doc.createDocumentFragment();
    for (const group of groups) {
      const listElement = doc.createElement(group.tag);
      for (const item of group.items) {
        listElement.appendChild(item);
      }
      fragment.appendChild(listElement);
    }
    list.replaceWith(fragment);
  }
};

/** Удаляет служебную разметку Quill: обёртки маркеров и внутренние атрибуты */
const stripServiceMarkup = (root: HTMLElement): void => {
  for (const ui of Array.from(root.querySelectorAll('span.ql-ui'))) {
    ui.remove();
  }
  for (const element of Array.from(root.querySelectorAll('*'))) {
    for (const attribute of SERVICE_ATTRIBUTES) {
      element.removeAttribute(attribute);
    }
  }
};

/** Неразрывный пробел, которым `getSemanticHTML()` заменяет обычные пробелы у границ тегов */
const NON_BREAKING_SPACE = '\u00A0';

/**
 * Возвращает неразрывные пробелы к обычным.
 *
 * Для хранимого текста они мусор: засоряют разметку и мешают переносу строк.
 */
const restoreRegularSpaces = (root: HTMLElement): void => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.nodeValue?.includes(NON_BREAKING_SPACE)) {
      node.nodeValue = node.nodeValue.replaceAll(NON_BREAKING_SPACE, ' ');
    }
  }
};

/**
 * Приводит вывод Quill к «чистому» HTML, пригодному для хранения и последующего рендера.
 * Возвращает пустую строку для документа без текста, чтобы пустой редактор не сохранял `<p><br></p>`.
 */
export const normalizeQuillHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return '';

  normalizeLists(root);
  stripServiceMarkup(root);
  restoreRegularSpaces(root);

  if (!root.textContent?.trim() && root.querySelectorAll('li').length === 0) return '';

  return sanitizeRichTextHtml(root.innerHTML);
};
